import { useCallback, useEffect, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import type { MentionEntity } from '@terra/shared-dto-chat-types';
import type {
  ChatObjectNavigationTarget,
  HITLEditInMainContentData,
  HttpViewerTabData,
} from '@terra/ui-design-system';
import { useLayoutStore, useMainContentTabs } from '../../store/layoutStore';
import { hitlEditCallbackRegistry } from '../../store/hitlEditCallbackRegistry';
import { useProxyStore } from '../../store/proxy-store';
import { useDetachedWindows } from '../../hooks/useDetachedWindows';
import { parseDetachedSearchParams } from '../../utils/detachedMode';
import {
  buildMainContentTabFromChatObject,
  buildMainContentTabFromMention,
} from '../../utils/chatMainContentTabUtils';
import { useMentions } from '../../hooks/useMentions';

export function useCopilotMainContentNavigation({
  chatId,
  linkedObject,
}: {
  chatId: string;
  linkedObject?: {
    kind: string;
    id: string;
    label?: string;
  };
}) {
  const tCopilot = useTranslations('chat.copilot');
  const tChatNavigation = useTranslations('chat.copilot.tabTitles');
  const tSkillEditor = useTranslations('customSkills.editor');
  const { openTab, openOrFocusTab, closeTab, navigateInActiveTab } =
    useMainContentTabs();
  const requests = useProxyStore((state) => state.requests);
  const setSelectedRequest = useProxyStore((state) => state.setSelectedRequest);
  const { setOnMentionClick } = useMentions();
  const { revealMainAlongsideCopilot } = useDetachedWindows();

  const mainContentTabFallbackTitles = useMemo(
    () => ({
      finding: tChatNavigation('findingDetails'),
      signal: tChatNavigation('signalDetails'),
      endpoint: tChatNavigation('endpoint'),
      webpage: tChatNavigation('webPage'),
      execution: tChatNavigation('execution'),
      skillEditor: tSkillEditor('createTitle'),
      requests: tChatNavigation('requests'),
    }),
    [tChatNavigation, tSkillEditor],
  );

  const revealMainContentIfDetachedCopilot = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (parseDetachedSearchParams(window.location.search).type !== 'copilot') {
      return;
    }

    void revealMainAlongsideCopilot();
  }, [revealMainAlongsideCopilot]);

  const handleMentionClick = useCallback(
    (mention: MentionEntity) => {
      const tab = buildMainContentTabFromMention({
        mention,
        fallbackTitles: mainContentTabFallbackTitles,
      });

      if (tab) {
        openOrFocusTab(tab);
      }
    },
    [mainContentTabFallbackTitles, openOrFocusTab],
  );

  useEffect(() => {
    setOnMentionClick(handleMentionClick);

    return () => {
      setOnMentionClick(undefined);
    };
  }, [setOnMentionClick, handleMentionClick]);

  const handleOpenChatObject = useCallback(
    (target: ChatObjectNavigationTarget) => {
      if (target.type === 'request') {
        if (requests[target.id]) {
          setSelectedRequest(requests[target.id]);
        }
      }

      const tab = buildMainContentTabFromChatObject({
        target,
        linkedObject,
        fallbackTitles: mainContentTabFallbackTitles,
      });

      if (tab) {
        openOrFocusTab(tab);
      }
    },
    [
      linkedObject,
      mainContentTabFallbackTitles,
      openOrFocusTab,
      requests,
      setSelectedRequest,
    ],
  );

  const handleOpenEditInMainContent = useCallback(
    (data: HITLEditInMainContentData) => {
      revealMainContentIfDetachedCopilot();
      hitlEditCallbackRegistry.register(data.hitlMessageId, {
        onEditComplete: data.onEditComplete,
        onApprove: data.onApprove,
        onReject: data.onReject,
      });
      const tabData: Parameters<typeof openOrFocusTab>[0] = {
        id: `hitl-edit-${data.hitlMessageId}`,
        title: data.objectType
          ? `Review ${
              data.objectType[0].toUpperCase() + data.objectType.slice(1)
            }`
          : `Edit ${data.toolName}`,
        type: 'hitl-edit',
        data: {
          toolName: data.toolName,
          args: data.args,
          hitlMessageId: data.hitlMessageId,
          threadId: chatId,
        },
      };

      openOrFocusTab(tabData);
    },
    [chatId, openOrFocusTab, revealMainContentIfDetachedCopilot],
  );

  const handleCloseEditInMainContent = useCallback(
    (hitlMessageId: string) => {
      hitlEditCallbackRegistry.unregister(hitlMessageId);
      const matchingReviewTabs = useLayoutStore
        .getState()
        .mainContentTabs.filter(
          (tab) =>
            tab.type === 'hitl-edit' &&
            (tab.data.hitlMessageId === hitlMessageId ||
              tab.data.hitlMessageId.startsWith(`${hitlMessageId}-`)),
        );

      if (matchingReviewTabs.length === 0) {
        closeTab(`hitl-edit-${hitlMessageId}`);

        return;
      }

      matchingReviewTabs.forEach((tab) => {
        closeTab(tab.id);
      });
    },
    [closeTab],
  );

  const handleOpenHttpInMainContent = useCallback(
    (data: HttpViewerTabData) => {
      revealMainContentIfDetachedCopilot();
      const tabTitle = data.title ?? 'HTTP Viewer';
      const tabId = `http-viewer-${Date.now()}`;

      openTab({
        id: tabId,
        title: tabTitle,
        type: 'http-viewer',
        data: {
          request: data.request,
          response: data.response,
          fallbackOutput: data.fallbackOutput,
          toolName: data.toolName,
          title: data.title,
        },
      });
    },
    [openTab, revealMainContentIfDetachedCopilot],
  );

  const handleOpenNodeArgsInMainContent = useCallback(
    (data: { nodeName: string; nodePayload: string }) => {
      const tabId = `node-args-${Date.now()}`;

      openTab({
        id: tabId,
        title: `Node: ${data.nodeName}`,
        type: 'node-args-viewer',
        data: {
          nodeName: data.nodeName,
          nodePayload: data.nodePayload,
        },
      });
    },
    [openTab],
  );

  const handleOpenBrowserStreamInMainContent = useCallback(
    (data: { streamingId: string }) => {
      revealMainContentIfDetachedCopilot();
      openTab({
        id: `browser-stream-${data.streamingId}`,
        title: 'Live Browser View',
        type: 'browser-stream',
        data: {
          streamingId: data.streamingId,
        },
      });
    },
    [openTab, revealMainContentIfDetachedCopilot],
  );

  const handleOpenPlanInMainContent = useCallback(
    (data: { filePath: string; title?: string }) => {
      revealMainContentIfDetachedCopilot();
      openOrFocusTab({
        id: `plan-viewer-${chatId}`,
        title: data.title || tCopilot('plan.tabTitle'),
        type: 'plan-viewer',
        data: { chatId, filePath: data.filePath },
      });
    },
    [chatId, openOrFocusTab, tCopilot, revealMainContentIfDetachedCopilot],
  );

  return {
    openTab,
    openOrFocusTab,
    navigateInActiveTab,
    revealMainContentIfDetachedCopilot,
    findingDetailsTabTitle: tChatNavigation('findingDetails'),
    handleOpenChatObject,
    handleOpenEditInMainContent,
    handleCloseEditInMainContent,
    handleOpenHttpInMainContent,
    handleOpenNodeArgsInMainContent,
    handleOpenBrowserStreamInMainContent,
    handleOpenPlanInMainContent,
  };
}
